/* Premium PDP adapters for the theme's existing PhotoSwipe; the vendor is unchanged. */
(() => {
  if (window.AdriaPdpZoom) return;
  const mobile = () => matchMedia('(max-width:767px)').matches;
  const extraZoom = root => root?.dataset.agZoomExtra !== 'false';
  const mobilePinch = root => root?.dataset.agZoomPinchMobile !== 'false';
  const zoomAllowed = root => extraZoom(root) || (mobile() && mobilePinch(root));
  const initial = (levels, root) => {
    const value = Number(root?.dataset.agZoomMobile ?? 20);
    return levels.fit * (mobile() ? 1 + Math.max(0, Math.min(50, Number.isFinite(value) ? value : 20)) / 100 : 1);
  };
  const options = ({main = false, root = null} = {}) => {
    const outside = root?.dataset.agZoomControls !== 'overlay';
    const inset = () => {
      const safe = root instanceof Element ? parseFloat(getComputedStyle(root).getPropertyValue('--ag-zoom-safe-bottom')) || 0 : 0;
      return Math.max(12, safe) + 50 + 8;
    };
    function toggle() {
      const slide = this.currSlide;
      if (!slide?.isZoomable()) return;
      const base = initial(slide.zoomLevels, root);
      slide.zoomTo(slide.currZoomLevel > base + .001 ? base : base * 2,
        undefined, 0);
      slide.panTo(slide.bounds.center.x, slide.bounds.center.y);
    }
    return {
      // PhotoSwipe's pinch correction uses initial as its minimum. Keep it at fit;
      // the adapter applies the independently configured viewing scale on activation.
      initialZoomLevel: levels => zoomAllowed(root) ? levels.fit : initial(levels, root),
      secondaryZoomLevel: levels => initial(levels, root) * (extraZoom(root) ? 2 : 1),
      maxZoomLevel: levels => initial(levels, root) * 3,
      pinchToClose: false, allowPanToNext: false, clickToCloseNonZoomable: false,
      imageClickAction: function() { if (extraZoom(root) && !mobile()) toggle.call(this); }, tapAction: false, doubleTapAction: extraZoom(root) ? toggle : false, bgClickAction: false,
      showHideAnimationType: 'none', zoomAnimationDuration: 180,
      ...(main ? {closeOnVerticalDrag:false,
        mainClass:'ag-pdp-main-zoom ' + (outside ? 'ag-pdp-controls--outside' : 'ag-pdp-controls--overlay'),
        paddingFn: (_viewport,item) => {
          const gap = outside && (!item?.type || item.type === 'image') ? inset() : 0;
          return {top:gap,left:0,right:0,bottom:gap};
        }} : {})
    };
  };
  function attach(lightbox, {embedded = false, stage = null, labels = {}} = {}) {
    const instance = () => lightbox.pswp || lightbox;
    const root = stage?.closest('[data-ag-pdp-purchase]') || {dataset:labels};
    const baseline = slide => initial(slide.zoomLevels, root);
    const canZoom = () => zoomAllowed(root);
    const touchIds = new Set();
    let blockedPinch = false, touchStart = null;
    const isPhoto = slide => slide?.content?.isImageContent();
    lightbox.addFilter('isContentZoomable', value => value && canZoom() && !blockedPinch);
    lightbox.on('keydown', event => {
      if (!extraZoom(root) && event.originalEvent.key?.toLowerCase() === 'z') event.preventDefault();
    });
    lightbox.on('wheel', event => {
      if (!extraZoom(root) && (event.originalEvent.ctrlKey || instance().options.wheelToZoom)) event.preventDefault();
    });
    const cursor = () => {
      const pswp = instance(), slide = pswp.currSlide;
      if (pswp.element) pswp.element.dataset.agZoomState = !extraZoom(root) ? 'off' :
        slide && slide.currZoomLevel > baseline(slide) + .001 ? 'out' : 'in';
    };
    lightbox.on('zoomPanUpdate', cursor);

    const atInitial = slide => !!slide && slide.currZoomLevel <= baseline(slide) + .001;
    let resizeState = null, lastMobile = mobile(), dismiss = null, dismissTimer = null;
    const resetDismiss = () => {
      clearTimeout(dismissTimer); dismissTimer = null; dismiss = null;
      instance().element?.style.removeProperty('transform');
      instance().element?.style.removeProperty('transition');
    };
    const offset = () => {
      const pswp = instance();
      if (!embedded || !pswp.element || !stage) return;
      const rect = stage.getBoundingClientRect();
      pswp.setScrollOffset(rect.left + window.scrollX, rect.top + window.scrollY);
    };
    const prepare = slide => {
      if (!slide || slide.agPdpPannable) return;
      const original = slide.isPannable;
      // At the viewing scale a horizontal drag belongs to navigation, even if
      // that scale is slightly above fit. Pinch/bounds keep their real geometry.
      slide.isPannable = function() { return !atInitial(this) && original.call(this); };
      slide.agPdpPannable = true;
    };
    const fill = () => {
      const slide = instance().currSlide;
      prepare(slide);
      if (!isPhoto(slide) || !slide.zoomLevels.elementSize?.x) return;
      if (canZoom()) slide.zoomTo(baseline(slide), undefined, 0);
      else slide.setZoomLevel(baseline(slide));
      slide.panTo(slide.bounds.center.x, slide.bounds.center.y);
      offset(); lastMobile = mobile();
    };
    // Runs before the engine applies the first transform, also for neighbouring
    // slides and when inactive slides reset. No fit-sized frame can enter a swipe.
    lightbox.on('initialZoomPan', ({slide}) => {
      prepare(slide);
      if (!isPhoto(slide) || !slide.zoomLevels.elementSize?.x) return;
      slide.setZoomLevel(baseline(slide));
      slide.pan.x = slide.bounds.center.x;
      slide.pan.y = slide.bounds.center.y;
    });
    lightbox.on('afterSetContent', ({slide}) => {
      // Clip only photographs, not video/3D content or the independent controls.
      slide.holderElement?.classList.toggle('ag-pdp-photo-slide', !!isPhoto(slide));
    });
    lightbox.on('change', () => { prepare(instance().currSlide); offset(); cursor(); });
    lightbox.on('afterInit', () => {
      lastMobile = mobile(); resizeState = null;
      const element = instance().element;
      for (const [name, label] of [['m-close',labels.agZoomClose || 'Close'],['m-arrowPrev',labels.agZoomPrevious || 'Previous image'],['m-arrowNext',labels.agZoomNext || 'Next image']]) {
        const custom = name === 'm-arrowPrev' ? ',.sf-pswp-button--arrow-prev' : name === 'm-arrowNext' ? ',.sf-pswp-button--arrow-next' : '';
        element?.querySelector('.pswp__button--' + name + custom)?.setAttribute('aria-label', label);
      }
      if (embedded) { element.removeAttribute('role'); element.removeAttribute('aria-modal'); }
      offset(); cursor();
    });
    lightbox.on('beforeResize', () => {
      const slide = instance().currSlide;
      if (!slide?.isZoomable()) return;
      resizeState = {slide, mobile:lastMobile, ratio:slide.currZoomLevel / slide.zoomLevels.fit,
        x:(instance().viewportSize.x / 2 - slide.pan.x) / slide.currZoomLevel,
        y:(instance().viewportSize.y / 2 - slide.pan.y) / slide.currZoomLevel};
    });
    lightbox.on('resize', () => {
      if (!canZoom()) { resizeState = null; fill(); cursor(); return; }
      const slide = instance().currSlide, previous = resizeState;
      resizeState = null;
      if (!previous || previous.slide !== slide || previous.mobile !== mobile()) {fill(); return;}
      prepare(slide);
      const zoom = Math.max(slide.zoomLevels.fit, Math.min(slide.zoomLevels.max, slide.zoomLevels.fit * previous.ratio));
      slide.zoomTo(zoom, undefined, 0);
      slide.panTo(instance().viewportSize.x / 2 - previous.x * zoom, instance().viewportSize.y / 2 - previous.y * zoom);
      offset(); lastMobile = mobile();
    });
    lightbox.on('pointerDown', event => {
      offset();
      const e = event.originalEvent, pswp = instance();
      if (e.pointerType === 'touch') touchIds.add(e.pointerId);
      const touches = e.touches?.length ?? touchIds.size;
      if (touches === 1 && !blockedPinch) {
        const slide = pswp.currSlide;
        touchStart = slide ? {slide, zoom:slide.currZoomLevel, x:slide.pan.x, y:slide.pan.y} : null;
      }
      if (mobile() && !mobilePinch(root) && touches > 1 && !blockedPinch) {
        blockedPinch = true;
        resetDismiss();
        pswp.animations.stopAll();
        // Cancel the first finger's pending drag, but let PhotoSwipe track both
        // pointer releases. No vendor edits; the next one-finger gesture is fresh.
        pswp.gestures._rafStopLoop();
        pswp.gestures.isDragging = false;
        pswp.gestures.isZooming = false;
        pswp.gestures.dragAxis = null;
        pswp.mainScroll.moveTo(pswp.mainScroll.getCurrSlideX());
        if (touchStart?.slide === pswp.currSlide) {
          pswp.currSlide.setZoomLevel(touchStart.zoom);
          pswp.currSlide.panTo(touchStart.x, touchStart.y);
        }
      }
      if (blockedPinch) return;
      if ((e.pointerType === 'touch' && !e.isPrimary) || e.touches?.length > 1) {
        // A second finger takes over without committing the partial horizontal swipe.
        resetDismiss();
        pswp.animations.stopMainScroll();
        pswp.mainScroll.moveTo(pswp.mainScroll.getCurrSlideX());
      } else if (!embedded && mobile() && e.isPrimary && atInitial(pswp.currSlide) && !e.target.closest('button,a,input')) {
        resetDismiss();
        dismiss = {id:e.pointerId,x:e.clientX,y:e.clientY,lastY:e.clientY,time:performance.now(),velocity:0,axis:null};
      }
    });
    lightbox.on('pointerMove', event => {
      if (blockedPinch) { event.preventDefault(); return; }
      if (!dismiss || event.originalEvent.pointerId !== dismiss.id) return;
      const e=event.originalEvent, dx=e.clientX-dismiss.x, dy=e.clientY-dismiss.y;
      if (!dismiss.axis && Math.max(Math.abs(dx),Math.abs(dy))>=10)
        dismiss.axis=Math.abs(dx)>=Math.abs(dy)?'x':'y';
      if (dismiss.axis==='x' || (!dismiss.axis && Math.abs(dx)>=Math.abs(dy))) return;
      event.preventDefault();
      const now=performance.now();
      dismiss.velocity=(e.clientY-dismiss.lastY)/Math.max(1,now-dismiss.time);
      dismiss.time=now;dismiss.lastY=e.clientY;
      instance().element.style.transform=`translateY(${Math.max(0,dy)}px)`;
    });
    lightbox.on('pointerUp', event => {
      const original = event.originalEvent;
      if (original.pointerType === 'touch') touchIds.delete(original.pointerId);
      if (blockedPinch) {
        event.preventDefault();
        if ((original.touches?.length ?? touchIds.size) === 0) {
          // Keep the guard active for any other handlers on this release.
          queueMicrotask(() => { blockedPinch = false; touchStart = null; });
        }
        return;
      }
      if (!dismiss || event.originalEvent.pointerId !== dismiss.id) return;
      const gesture=dismiss,e=event.originalEvent;dismiss=null;
      if (gesture.axis!=='y') return;
      event.preventDefault();
      const dy=e.clientY-gesture.y;
      if (!e.type.includes('cancel') && (dy>=Math.min(100,instance().viewportSize.y*.2) ||
        (dy>=40 && performance.now()-gesture.time<120 && gesture.velocity>.6))) {
        instance().close();
      } else {
        const element=instance().element;
        element.style.transition=matchMedia('(prefers-reduced-motion:reduce)').matches?'none':'transform 180ms ease-out';
        element.style.transform='translateY(0)';
        dismissTimer=setTimeout(resetDismiss,190);
      }
    });
    lightbox.on('destroy', () => { resetDismiss(); touchIds.clear(); blockedPinch = false; touchStart = null; });
    return {fill, offset, atInitial, isGestureBlocked:() => blockedPinch};
  }
  window.AdriaPdpZoom = {options, attach, initial};
})();
